// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Component dependencies.
import { HybridButton } from '../buttons.jsx';

// Shared utils for Display Items.
import * as utils from './utils.jsx';

// Model objects.
import DisplayItem from '../../model/entity/DisplayItem/DisplayItem.js';

// Misc dependencies.
import { getLabel } from '../../utils/string.js';

/**
 * <pre>
 * Display Item > Claim related React components.
 *
 * </pre>
 *
 * @author Various
 * @namespace Common.Components.DisplayItems.Claim
 */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Components */

/**
 * Rights Claim Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Claim
 */
class RightsClaim extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleGrant = this.handleGrant.bind(this);
    this.handleDeny = this.handleDeny.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const { item, templateOpts, itemOpts } = this.props;

    const _profile = utils.getItemProfile({ item }),
      _mainContent = utils.getItemContent({ item, type: 'main' }),
      _secondaryContent = utils.getItemContent({ item, type: 'secondary' }),
      _networkIcon = utils.getNetworkIcon(item.meta.network),
      _timestamp = utils.getItemTimestamp({ item }),
      _itemAsset = utils.getItemAsset({ item, assetUrl: this.props.assetUrl, assetUgcUrl: this.props.assetUgcUrl });

    const _grantButton = (
      <HybridButton
        className="xButton xManageRights xGrantRights"
        dataAttributes={{ 'data-ngx-rights-action': 'rights:grant' }}
        onClick={this.handleGrant}
      >
        {getLabel('rights.buttons.grant')}
      </HybridButton>
    );

    const _denyButton = (
      <HybridButton
        className="xButton xManageRights xDenyRights"
        dataAttributes={{ 'data-ngx-rights-action': 'rights:deny' }}
        onClick={this.handleDeny}
      >
        {getLabel('rights.buttons.deny')}
      </HybridButton>
    );

    const innerStyle = {};

    const displayItemModel = {
      'data-ngx-tags': item.meta.tags,
      'data-ngx-id':   item.id,
      onClick:         () => {
        this.props.onClick(item);
      },
    };

    return (
      <div {...displayItemModel} className={utils.getClassNamesForItem({ item, templateOpts })}>
        <div className="xItemInner" style={innerStyle}>
          {_networkIcon}
          {_profile}
          {_itemAsset}
          {_mainContent}
          {_secondaryContent}
          <div className="xCTAContainer">
            {_grantButton}
            {_denyButton}
          </div>
        </div>
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when Grant button is clicked.
   *
   * @param {object} event The click event object.
   */
  handleGrant(event) {
    if (this.props.onClick.grant) {
      this.props.onClick.grant(event, this.props.item);
    }
  }

  /**
   * Handler called when Deny button is clicked.
   *
   * @param {object} event The click event object.
   */
  handleDeny(event) {
    if (this.props.onClick.deny) {
      this.props.onClick.deny(event, this.props.item);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem object.
 */
RightsClaim.propTypes = {
  item: PropTypes.instanceOf(DisplayItem),
};

/**
 * React default props.
 *
 * @type {Object}
 */
RightsClaim.defaultProps = {
  item:         {},
  templateOpts: {},
  itemOpts:     {},
};

export { RightsClaim };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * Content Claim Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Claim
 */
class ContentClaim extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleClaim = this.handleClaim.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const item = this.props.item;

    const _profile = utils.getItemProfile({ item }),
      _mainContent = utils.getItemContent({ item, type: 'main' }),
      _secondaryContent = utils.getItemContent({ item, type: 'secondary' }),
      _networkIcon = utils.getNetworkIcon(item.meta.network),
      _timestamp = utils.getItemTimestamp({ item }),
      _itemAsset = utils.getItemAsset({ item, assetUrl: this.props.assetUrl, assetUgcUrl: this.props.assetUgcUrl });

    const _claimButton = (
      <HybridButton dataAttributes={{ 'data-ngx-claim-action': 'content:claim' }} onClick={this.handleClaim}>
        {this.props.item.content.linkTitle || this.props.item.content.linkText}
      </HybridButton>
    );

    const innerStyle = {};

    const displayItemModel = {
      'data-ngx-tags': item.meta.tags,
      'data-ngx-id':   item.id,
      onClick:         () => {
        this.props.onClick(this.props.item);
      },
    };

    return (
      <div
        {...displayItemModel}
        className={utils.getClassNamesForItem({ item: this.props.item, templateOpts: this.props.templateOpts })}
      >
        <div className="xItemInner" style={innerStyle}>
          {_profile}
          {_mainContent}
          {_secondaryContent}
          {_timestamp}
          {_itemAsset}
          {_networkIcon}
          <div className="xCTAContainer">{_claimButton}</div>
        </div>
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when Claim button is clicked.
   *
   * @param {object} event The click event object.
   */
  handleClaim(event) {
    if (this.props.onClick) {
      this.props.onClick(event, this.props.item);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem object.
 */
ContentClaim.propTypes = {
  item: PropTypes.instanceOf(DisplayItem),
};

/**
 * React default props.
 *
 * @type {Object}
 */
ContentClaim.defaultProps = {
  item:         {},
  templateOpts: {},
  itemOpts:     {},
};

export { ContentClaim };

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private utility functions */
